import { createFileRoute } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useMembership } from '@/api/hooks/useMembership'
import { useOrganization } from '@/api/hooks/useOrganization'
import { useUpdateOrganization } from '@/api/hooks/useUpdateOrganization'
import PageLayout from '@/components/layout/page-layout'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'

export const Route = createFileRoute('/_authenticated/organization-settings')({
  component: OrganizationSettingsPage,
})

type FormValues = {
  receiptManagerEmail: string
  baselineBudgetSek: string
  friskvardBaselineBudgetSek: string
}

const parseBaselineBudget = (value: string): number => {
  const parsed = Number(value.trim())
  return Number.isNaN(parsed) || parsed < 0 ? 0 : parsed
}

function OrganizationSettingsPage() {
  const { t } = useTranslation()
  const { isAdmin } = useMembership()
  const organization = useOrganization()
  const { mutateAsync: updateOrganization, isPending } = useUpdateOrganization()

  const form = useForm<FormValues>({
    values: {
      receiptManagerEmail: organization?.receiptManagerEmail ?? '',
      baselineBudgetSek: String(organization?.baselineBudgetSek ?? 0),
      friskvardBaselineBudgetSek: String(organization?.friskvardBaselineBudgetSek ?? 0),
    },
  })

  const onSubmit = form.handleSubmit(
    async ({ receiptManagerEmail, baselineBudgetSek, friskvardBaselineBudgetSek }) => {
      const parsedBaseline = parseBaselineBudget(baselineBudgetSek)
      const parsedFriskvardBaseline = parseBaselineBudget(friskvardBaselineBudgetSek)
      try {
        await updateOrganization({
          requestBody: {
            receiptManagerEmail,
            baselineBudgetSek: parsedBaseline,
            friskvardBaselineBudgetSek: parsedFriskvardBaseline,
          },
        })
        form.reset({
          receiptManagerEmail,
          baselineBudgetSek: String(parsedBaseline),
          friskvardBaselineBudgetSek: String(parsedFriskvardBaseline),
        })
        toast.success(t('organizationSettingsPage.savedToast'))
      } catch (error) {
        toast.error((error as Error).message)
      }
    },
  )

  return (
    <PageLayout title={t('organizationSettingsPage.title')}>
      {organization && (
        <div className="card">
          <div className="flex items-center gap-4">
            <Avatar className="size-14 rounded-md">
              <AvatarImage
                className="rounded-full object-cover"
                alt={organization.name}
                src={organization.image?.image}
              />
              <AvatarFallback className="rounded-full">
                {organization.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="heading-4">{organization.name}</span>
              <span className="body-2 text-text-secondary">{organization.organizationNumber}</span>
            </div>
          </div>

          {/* Organization-wide settings belong to admins. A department manager reads them — the
              baselines explain where their members' budgets come from — but cannot change them. */}
          <form className="flex flex-col gap-4" onSubmit={onSubmit}>
            {!isAdmin && (
              <p className="body-2 text-text-secondary">
                {t('organizationSettingsPage.readOnlyNotice')}
              </p>
            )}
            <Field>
              <FieldLabel htmlFor="receiptManagerEmail">
                {t('organizationSettingsPage.receiptManagerEmailLabel')}
              </FieldLabel>
              <Input
                disabled={!isAdmin}
                id="receiptManagerEmail"
                type="email"
                {...form.register('receiptManagerEmail')}
              />
              <FieldError>{form.formState.errors.receiptManagerEmail?.message}</FieldError>
            </Field>
            <Field>
              <FieldLabel htmlFor="baselineBudgetSek">
                {t('organizationSettingsPage.baselineBudgetLabel')}
              </FieldLabel>
              <Input
                disabled={!isAdmin}
                id="baselineBudgetSek"
                type="number"
                inputMode="numeric"
                min={0}
                {...form.register('baselineBudgetSek')}
              />
              <FieldDescription>
                {t('organizationSettingsPage.baselineBudgetHint')}
              </FieldDescription>
            </Field>
            <Field>
              <FieldLabel htmlFor="friskvardBaselineBudgetSek">
                {t('organizationSettingsPage.friskvardBaselineBudgetLabel')}
              </FieldLabel>
              <Input
                disabled={!isAdmin}
                id="friskvardBaselineBudgetSek"
                type="number"
                inputMode="numeric"
                min={0}
                {...form.register('friskvardBaselineBudgetSek')}
              />
              <FieldDescription>
                {t('organizationSettingsPage.friskvardBaselineBudgetHint')}
              </FieldDescription>
            </Field>
            {isAdmin && (
              <div className="flex justify-end">
                <Button type="submit" disabled={!form.formState.isDirty} isLoading={isPending}>
                  {t('global.save')}
                </Button>
              </div>
            )}
          </form>
        </div>
      )}
    </PageLayout>
  )
}
